import React, { useState } from 'react';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { Broadcast, BroadcastAttachment, BroadcastEditorModes, BroadcastFormValues } from 'modules/broadcasts/types';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { broadcastSchema } from 'modules/broadcasts/schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Container, Grid2 } from '@mui/material';
import { DateField, InputField, QuillInputField, RadioGroupField } from 'components/form';
import { DropzoneAreaBase, FileObject } from 'mui-file-dropzone';
import { AttachmentsSection } from './AttachmentsSection';
import { getBroadcastTypeOptions, getUpsertBroadcastInput, mapFileToBroadcastAttachment } from 'modules/broadcasts/lib';
import { broadcastApi } from 'modules/broadcasts/api';
import { useMutation } from '@apollo/client';
import { Account, UserProfile } from 'types';
import { useHistory } from 'react-router-dom';
import { useNotifications } from 'modules/notification';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { GlobalBroadcastSettings } from './GlobalBroadcastSettings';
import { useSelector } from 'react-redux';
import { AdminRole, BroadcastStatus, BroadcastType } from '@trnsact/trnsact-shared-types';

interface Props {
  account: Account;
  broadcast: Broadcast;
  mode: BroadcastEditorModes;
}

export const CreateEditBroadcastForm = ({ broadcast, account, mode }: Props) => {
  const { goBack } = useHistory();

  const { showNotification } = useNotifications();
  const { handleOpen: openViewBroadcastDialog } = useModal(ModalsKeys.ViewBroadcastDialog);

  const [upsertBroadcast] = useMutation(broadcastApi.upsertBroadcast);
  const [uploadAttachments] = useMutation(broadcastApi.uploadAttachments);
  const [removeAttachments] = useMutation(broadcastApi.removeAttachments);

  const userProfile = useSelector((state: any) => state.userProfile) as UserProfile;

  const [attachmentsToUpload, setAttachmentsToUpload] = useState<File[]>([]);
  const [attachmentIdsToRemove, setAttachmentIdsToRemove] = useState<string[]>([]);
  const [isPublishUpsertLoading, setIsPublishUpsertLoading] = useState<boolean>(false);
  const [isDraftUpsertLoading, setIsDraftUpsertLoading] = useState<boolean>(false);

  const form = useForm<BroadcastFormValues>({
    resolver: zodResolver(broadcastSchema),
    defaultValues: {
      type: broadcast?.type ?? BroadcastType.Product,
      title: broadcast?.title ?? '',
      effectiveDateTime: broadcast?.effectiveDateTime ?? Date.now(),
      body: broadcast?.body ?? '',
      attachments: broadcast?.attachments ?? [],
      isGlobal: broadcast?.accountId === null,
      audienceType: broadcast?.broadcastAudience?.[0]?.audienceType,
    },
  });

  const {
    fields: attachments,
    append: appendAttachments,
    remove: removeAttachment,
  } = useFieldArray({ control: form.control, name: 'attachments' });

  const handleSend = async (formValues: BroadcastFormValues) => {
    await saveBroadcast(BroadcastStatus.Published, formValues, setIsPublishUpsertLoading);
  };

  const handleSaveAsDraft = async () => {
    await saveBroadcast(BroadcastStatus.Draft, form.getValues(), setIsDraftUpsertLoading);
  };

  const saveBroadcast = async (
    status: BroadcastStatus,
    formValues: BroadcastFormValues,
    loadingStateSetter: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    try {
      loadingStateSetter(true);
      let broadcastId = broadcast?.broadcastId;
      const upsertBroadcastInput = getUpsertBroadcastInput(broadcastId, account.id, status, formValues);
      const upsertBroadcastResponse = await upsertBroadcast({
        variables: {
          input: upsertBroadcastInput,
        },
      });
      broadcastId = broadcastId || upsertBroadcastResponse?.data?.broadcast?.upsertBroadcast?.entity?.broadcastId;

      if (attachmentsToUpload.length) {
        await uploadAttachments({
          variables: {
            input: {
              attachments: attachmentsToUpload,
              broadcastId,
            },
          },
        });
      }

      if (attachmentIdsToRemove.length) {
        await removeAttachments({
          variables: {
            input: {
              broadcastAttachmentIds: attachmentIdsToRemove,
            },
          },
        });
      }
      showNotification(`Broadcast successfully ${mode === BroadcastEditorModes.Edit ? 'updated' : 'created'}!`);
      goBack();
    } catch (error) {
      console.error(error);
      showNotification('Something went wrong', { type: 'error' });
    } finally {
      loadingStateSetter(false);
    }
  };

  const handleView = () => {
    openViewBroadcastDialog({
      broadcastPreview: {
        ...broadcast,
        ...form.getValues(),
      },
    });
  };

  const onAddFiles = (files: FileObject[]) => {
    setAttachmentsToUpload(files.map(file => file.file));
    appendAttachments(files.map(mapFileToBroadcastAttachment));
  };

  const onRemoveAttachment = (attachment: BroadcastAttachment, idx: number) => {
    if (attachment.broadcastAttachmentId) {
      setAttachmentIdsToRemove([...attachmentIdsToRemove, attachment.broadcastAttachmentId]);
    }
    removeAttachment(idx);
  };

  const isSuperAdmin = userProfile.adminRole === AdminRole.Super;
  const isGlobalBroadcastSettingsVisible = isSuperAdmin && !broadcast?.accountId;

  const broadcastTypeOptions = getBroadcastTypeOptions(isSuperAdmin);

  return (
    <CardContainer>
      <FormProvider {...form}>
        <Container maxWidth="lg">
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <RadioGroupField row label="Type" control={form.control} options={broadcastTypeOptions} name="type" />
            </Grid2>
            <Grid2 container size={12}>
              <Grid2 size={8}>
                <InputField
                  label="Title"
                  control={form.control}
                  name="title"
                  inputProps={{ variant: 'outlined', required: true }}
                />
              </Grid2>
              <Grid2 size={4}>
                <DateField
                  label="Effective Date"
                  control={form.control}
                  name="effectiveDateTime"
                  slotProps={{ textField: { variant: 'outlined', size: 'small', required: true } }}
                />
              </Grid2>
            </Grid2>
            <Grid2 size={12}>
              <QuillInputField
                control={form.control}
                setValue={form.setValue}
                getValues={form.getValues}
                name="body"
                placeholder="Summary (Supports formatted text, images, and HTML paste)"
                style={{ minHeight: '150px' }}
              />
            </Grid2>
            <Grid2 size={12}>
              <DropzoneAreaBase
                fileObjects={[]}
                onAdd={onAddFiles}
                dropzoneText={'Drag & drop or Choose File'}
                maxFileSize={25000000}
                showPreviews={false}
                showPreviewsInDropzone={false}
                filesLimit={10}
              />
            </Grid2>
            <Grid2 size={12}>
              <AttachmentsSection attachments={attachments} removeAttachment={onRemoveAttachment} />
            </Grid2>
            {isGlobalBroadcastSettingsVisible && (
              <Grid2 size={12}>
                <GlobalBroadcastSettings mode={mode} />
              </Grid2>
            )}
            <Grid2 container size={12} spacing={1}>
              <Button
                variant="contained"
                onClick={form.handleSubmit(handleSend)}
                size="small"
                loading={isPublishUpsertLoading}
                disabled={isDraftUpsertLoading}
              >
                Send
              </Button>
              <Button
                variant="outlined"
                onClick={handleSaveAsDraft}
                size="small"
                loading={isDraftUpsertLoading}
                disabled={isPublishUpsertLoading}
              >
                Save as Draft
              </Button>
              <Button
                variant="outlined"
                onClick={handleView}
                size="small"
                disabled={isPublishUpsertLoading || isDraftUpsertLoading}
              >
                View
              </Button>
              <Button
                variant="text"
                onClick={goBack}
                size="small"
                sx={{ ml: 'auto' }}
                disabled={isPublishUpsertLoading || isDraftUpsertLoading}
              >
                Cancel
              </Button>
            </Grid2>
          </Grid2>
        </Container>
      </FormProvider>
    </CardContainer>
  );
};
